import { Link, useParams } from "react-router-dom";
import { ChevronRight, Lock, LogIn } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { PageHeader } from "@/components/content/PageHeader";
import { EmptyState } from "@/components/content/EmptyState";
import { PaywallNotice } from "@/components/content/PaywallNotice";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useChapters } from "@/hooks/useCurriculum";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { StaggerList, StaggerItem } from "@/components/motion/Stagger";
import { cn } from "@/lib/cn";

export function ChaptersPage() {
  const { subjectId } = useParams<{ subjectId: string }>();
  const { status } = useAuth();
  const { data: me } = useCurrentUser();
  const { data: chapters, isLoading, isError } = useChapters(subjectId);

  const firstChapter = chapters?.[0];
  const subjectName = firstChapter?.subject_id ? "" : ""; // server returns names on each chapter

  // Entitlement: user is unlocked if (auth + active sub + sub class matches this chapter's class)
  // For v1, the backend signed-URL endpoint is the source of truth - this UI is purely indicative.
  const userClass = me?.class_id;

  return (
    <Container size="lg" className="py-8 lg:py-12">
      <PageHeader
        title={firstChapter?.subject_id ? "Chapters" : "Chapters"}
        crumbs={[
          { to: "/subjects", label: "Subjects" },
          { label: subjectName || "..." },
        ]}
        description={
          status === "anonymous"
            ? "First chapter is free. Subscribe to unlock the rest."
            : undefined
        }
      />

      {status === "anonymous" ? <PaywallNotice className="mb-8" /> : null}

      {isLoading ? (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface dark:bg-bg">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-5 w-48" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <EmptyState title="Couldn't load chapters" body="Refresh or try again shortly." />
      ) : !chapters || chapters.length === 0 ? (
        <EmptyState
          title="No chapters yet"
          body="Content for this subject is on the way."
        />
      ) : (
        <StaggerList
          className="divide-y divide-border rounded-xl border border-border bg-surface dark:bg-bg overflow-hidden w-full"
          role="list"
          gap={0.025}
        >
          {chapters.map((c, idx) => {
            const isDemo = c.is_demo === true;
            const hasAccess =
              isDemo ||
              (status === "authenticated" && userClass === c.class_id && !!me?.active_subscription_id);
            // For locked chapters, deep-link to the picker page so the
            // signed-URL endpoint can sign-or-paywall per asset. We
            // route via the same /study/:id link whether the user can
            // open it or not -- the viewer pages themselves render the
            // sign-in vs subscribe CTA via useChapterAccess.
            const targetHref = `/study/${encodeURIComponent(c.chapter_id)}`;
            const needsSignIn = !hasAccess && status === "anonymous";
            return (
              <StaggerItem key={c.chapter_id} role="listitem" className="block w-full min-w-0">
                <Link
                  to={targetHref}
                  className={cn(
                    "flex w-full items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 transition-colors",
                    "hover:bg-surface-2 active:bg-surface-3",
                    "focus-visible:outline-none focus-visible:bg-surface-2",
                  )}
                >
                  <span
                    className={cn(
                      "grid h-7 w-7 sm:h-8 sm:w-8 place-items-center rounded-md text-[11px] sm:text-xs font-semibold tabular-nums flex-shrink-0",
                      isDemo
                        ? "bg-brand-soft text-brand"
                        : "bg-surface-2 text-text-muted",
                    )}
                  >
                    {idx + 1}
                  </span>

                  {/* Name -- wrapped so the truncation context is bullet-proof:
                      the flex-1 min-w-0 sits on the wrapper, the actual span
                      gets `block truncate` so `white-space: nowrap` engages
                      and the text ellipses instead of pushing siblings off
                      the viewport on narrow screens. */}
                  <div className="flex-1 min-w-0">
                    <span className="block truncate text-[15px] sm:text-base font-medium text-text">
                      {c.chapter_name}
                    </span>
                  </div>

                  {/* Access state. On very narrow screens we collapse to
                      an icon-only chip; on sm+ we show the labelled badge. */}
                  {isDemo ? (
                    <>
                      <span className="flex-shrink-0 hidden sm:inline-flex">
                        <Badge tone="brand">Free</Badge>
                      </span>
                      <span
                        aria-label="Free chapter"
                        className="sm:hidden flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-brand"
                      >
                        Free
                      </span>
                    </>
                  ) : needsSignIn ? (
                    <>
                      <span className="flex-shrink-0 hidden sm:inline-flex">
                        <Badge tone="neutral" className="gap-1">
                          <LogIn className="h-3 w-3" />
                          Sign in
                        </Badge>
                      </span>
                      <span
                        aria-label="Sign in to unlock"
                        className="sm:hidden grid place-items-center h-7 w-7 rounded-full bg-surface-2 text-text-muted flex-shrink-0"
                      >
                        <LogIn className="h-3.5 w-3.5" />
                      </span>
                    </>
                  ) : !hasAccess ? (
                    <>
                      <span className="flex-shrink-0 hidden sm:inline-flex">
                        <Badge tone="neutral" className="gap-1">
                          <Lock className="h-3 w-3" />
                          Premium
                        </Badge>
                      </span>
                      <span
                        aria-label="Premium chapter"
                        className="sm:hidden grid place-items-center h-7 w-7 rounded-full bg-surface-2 text-text-muted flex-shrink-0"
                      >
                        <Lock className="h-3.5 w-3.5" />
                      </span>
                    </>
                  ) : null}

                  <ChevronRight className="h-4 w-4 text-text-subtle flex-shrink-0" />
                </Link>
              </StaggerItem>
            );
          })}
        </StaggerList>
      )}
    </Container>
  );
}
